/**
 * Form state helpers for profile editing.
 * Maps API Profile objects to editable values and builds minimal PATCH payloads
 * with only changed standard and custom fields.
 */
import type { Profile, ProfilePatch } from "@spork/s4p-server-sdk";
import type { ProfileField } from "../types/profileFieldInput";

import { isStandardFieldReadOnly } from "./isStandardFieldReadOnly";

export type ProfileFormValues = {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    custom_fields: Record<string, string | number | number[] | null>;
};

/**
 * Convert custom field from API response to simpler object with only one property: value.
 * @param field
 * @returns
 */
function getPeopleOrEnumFormValue(
    value: ProfileField["value"]
): number | null {
    if (typeof value === "number") {
        return value;
    }
    if (value && typeof value === "object" && "id" in value) {
        return (value as { id: number }).id;
    }
    return null;
}

function getCustomFieldFormValue(
    field: ProfileField
): string | number | number[] | null {
    if (field.field_type === "enum" || field.field_type === "people") {
        return getPeopleOrEnumFormValue(field.value);
    }

    if (field.field_type === "set") {
        if (Array.isArray(field.value)) {
            return field.value
                .map((item: unknown) => {
                    if (typeof item === "number") {
                        return item;
                    }
                    if (typeof item === "string") {
                        const parsed = Number(item.trim());
                        return Number.isNaN(parsed) ? null : parsed;
                    }
                    if (item && typeof item === "object" && "id" in item) {
                        return (item as { id: number }).id;
                    }
                    return null;
                })
                .filter((id: number | null): id is number => id != null);
        }
        return [];
    }

    if (field.value == null) {
        return "";
    }

    if (typeof field.value === "string" || typeof field.value === "number") {
        return String(field.value);
    }

    return field.text ?? "";
}

export function createProfileFormValues(person: Profile): ProfileFormValues {
    const custom_fields: Record<string, string | number | number[] | null> = {};

    for (const [fieldCode, field] of Object.entries(person.custom_fields) as [
        string,
        ProfileField,
    ][]) {
        custom_fields[fieldCode] = getCustomFieldFormValue(field);
    }

    return {
        first_name: person.first_name ?? "",
        last_name: person.last_name ?? "",
        email: person.email ?? "",
        phone: person.phone ?? "",
        custom_fields,
    };
}

function normalizeString(value: string): string | undefined {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
}

function valuesEqual(
    current: string | number | number[] | null,
    original: string | number | number[] | null
): boolean {
    if (Array.isArray(current) || Array.isArray(original)) {
        const currentIds = Array.isArray(current) ? [...current].sort() : [];
        const originalIds = Array.isArray(original) ? [...original].sort() : [];
        if (currentIds.length !== originalIds.length) {
            return false;
        }
        return currentIds.every((value, index) => value === originalIds[index]);
    }

    return current === original;
}

/** True when editable form values differ from the loaded profile (would produce a PATCH). */
export function isProfileFormDirty(
    person: Profile,
    values: ProfileFormValues
): boolean {
    return Object.keys(buildProfilePatch(person, values)).length > 0;
}

export function buildProfilePatch(
    person: Profile,
    values: ProfileFormValues
): ProfilePatch {
    const patch: ProfilePatch = {};
    const original = createProfileFormValues(person);

    if (
        !isStandardFieldReadOnly(person, "first_name") &&
        values.first_name !== original.first_name
    ) {
        patch.first_name = normalizeString(values.first_name);
    }

    if (
        !isStandardFieldReadOnly(person, "last_name") &&
        values.last_name !== original.last_name
    ) {
        patch.last_name = normalizeString(values.last_name);
    }

    if (person.role !== "student") {
        if (values.email !== original.email) {
            patch.email = normalizeString(values.email);
        }

        if (values.phone !== original.phone) {
            patch.phone = normalizeString(values.phone);
        }
    }

    const customFieldPatch: NonNullable<ProfilePatch["custom_fields"]> = {};

    for (const [fieldCode, field] of Object.entries(person.custom_fields) as [
        string,
        ProfileField,
    ][]) {
        if (field.read_only) {
            continue;
        }

        const currentValue = values.custom_fields[fieldCode] ?? null;
        const originalValue = original.custom_fields[fieldCode] ?? null;

        if (!valuesEqual(currentValue, originalValue)) {
            if (field.field_type === "enum") {
                customFieldPatch[fieldCode] =
                    typeof currentValue === "number" ? currentValue : null;
            } else if (field.field_type === "people") {
                // Pipedrive v2 people fields expect a bare person id, not `{ id }`.
                customFieldPatch[fieldCode] =
                    typeof currentValue === "number" ? currentValue : null;
            } else if (field.field_type === "set") {
                customFieldPatch[fieldCode] = Array.isArray(currentValue)
                    ? currentValue
                    : [];
            } else if (typeof currentValue === "string") {
                customFieldPatch[fieldCode] =
                    normalizeString(currentValue) ?? "";
            } else {
                customFieldPatch[fieldCode] = currentValue;
            }
        }
    }

    if (Object.keys(customFieldPatch).length > 0) {
        patch.custom_fields = customFieldPatch;
    }

    return patch;
}
